import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField, Chip, IconButton, CircularProgress, Alert, Paper } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../utils/api";

const STATUS_COLOR = { pending:"#b45309", reviewed:"#2f63ad", upheld:"#dc2626", overturned:"#16a34a", dismissed:"#6b778c" };

const ViolationAppealsTab = ({ violationId }) => {
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [reason,  setReason]  = useState("");
  const [submittedBy, setSubmittedBy] = useState("");
  const [reviewDialog, setReviewDialog] = useState(null);
  const [outcome, setOutcome] = useState("upheld");
  const [outcomeNotes, setOutcomeNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/violations/${violationId}/appeals`); setAppeals(r.data||[]); }
    catch { console.error("Failed to load appeals"); }
    finally { setLoading(false); }
  }, [violationId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    try { await api.post(`/violations/${violationId}/appeals`, { reason, submitted_by: submittedBy }); setReason(""); setSubmittedBy(""); setShowAdd(false); load(); }
    catch (err) { console.error("Failed to submit appeal:", err); }
  };

  const handleReview = async () => {
    try { await api.patch(`/violation-appeals/${reviewDialog}/review`, { outcome, outcome_notes: outcomeNotes }); setReviewDialog(null); setOutcome("upheld"); setOutcomeNotes(""); load(); }
    catch (err) { console.error("Failed to review appeal:", err); }
  };

  return (
    <Box>
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:2 }}>
        <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>Appeals</Typography>
        {!showAdd && <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={()=>setShowAdd(true)}
          sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad", boxShadow:"none" }}>Log Appeal</Button>}
      </Box>

      {showAdd && (
        <Paper elevation={0} sx={{ p:2, mb:2, borderRadius:"12px", border:"1px solid rgba(47,99,173,0.12)", bgcolor:"rgba(47,99,173,0.02)" }}>
          <TextField fullWidth size="small" label="Submitted By" value={submittedBy} onChange={e=>setSubmittedBy(e.target.value)} sx={{ mb:1.5, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
          <TextField fullWidth multiline rows={3} size="small" label="Appeal Reason *" value={reason} onChange={e=>setReason(e.target.value)} sx={{ mb:1.5, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
          <Box sx={{ display:"flex", gap:1 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={!reason.trim()} sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad", boxShadow:"none" }}>Submit Appeal</Button>
            <Button variant="outlined" onClick={()=>setShowAdd(false)} sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          </Box>
        </Paper>
      )}

      {/* Review dialog */}
      {reviewDialog && (
        <Paper elevation={0} sx={{ p:2, mb:2, borderRadius:"12px", border:"1px solid rgba(47,99,173,0.12)", bgcolor:"rgba(47,99,173,0.02)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1.5 }}>Review Appeal</Typography>
          <Box sx={{ display:"flex", gap:1, mb:1.5, flexWrap:"wrap" }}>
            {["upheld","overturned","dismissed"].map(o => (
              <Chip key={o} label={o.charAt(0).toUpperCase()+o.slice(1)} onClick={()=>setOutcome(o)}
                sx={{ fontWeight:700, cursor:"pointer", bgcolor: outcome===o ? `${STATUS_COLOR[o]}20` : "transparent",
                  color: outcome===o ? STATUS_COLOR[o] : "#6b778c", border:`1px solid ${outcome===o ? STATUS_COLOR[o] : "rgba(0,0,0,0.1)"}` }} />
            ))}
          </Box>
          <TextField fullWidth multiline rows={2} size="small" label="Outcome Notes" value={outcomeNotes} onChange={e=>setOutcomeNotes(e.target.value)} sx={{ mb:1.5, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
          <Box sx={{ display:"flex", gap:1 }}>
            <Button variant="contained" onClick={handleReview} sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad", boxShadow:"none" }}>Save Review</Button>
            <Button variant="outlined" onClick={()=>setReviewDialog(null)} sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          </Box>
        </Paper>
      )}

      {loading ? <CircularProgress size={24} /> : appeals.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius:"10px" }}>No appeals have been filed for this violation.</Alert>
      ) : (
        <Box sx={{ display:"flex", flexDirection:"column", gap:1.5 }}>
          {appeals.map(a => (
            <Box key={a.id} sx={{ p:2, borderRadius:"12px", border:"1px solid rgba(47,99,173,0.1)", bgcolor:"rgba(47,99,173,0.02)" }}>
              <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", mb:1 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>
                    {a.submitted_by || "Anonymous"}
                  </Typography>
                  <Typography variant="caption" sx={{ color:"#9ca3af" }}>
                    Filed: {new Date(a.submitted_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                  <Chip size="small" label={a.outcome || a.status}
                    sx={{ fontWeight:700, fontSize:11, borderRadius:"8px",
                      bgcolor: `${STATUS_COLOR[a.outcome || a.status] || "#6b778c"}15`,
                      color: STATUS_COLOR[a.outcome || a.status] || "#6b778c" }} />
                  {a.status === "pending" && (
                    <Button size="small" variant="outlined" onClick={()=>setReviewDialog(a.id)}
                      sx={{ borderRadius:"8px", textTransform:"none", fontWeight:600, fontSize:11 }}>Review</Button>
                  )}
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color:"#374151", mb: a.outcome_notes ? 0.5 : 0 }}>{a.reason}</Typography>
              {a.outcome_notes && <Typography variant="caption" sx={{ color:"#6b778c", fontStyle:"italic" }}>Outcome: {a.outcome_notes}</Typography>}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ViolationAppealsTab;
